import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import moment from 'moment-timezone';
import SplitShiftSegmentRow from './SplitShiftSegmentRow';
import ArrowSplitIconWhite from '../../../assets/images/arrow-split-white.svg';
import ArrowSplitIconPurple from '../../../assets/images/arrow-split-purple.svg';
import { useSplitShiftMutation } from '../../../redux/globalOverview';
import { showNotification } from '../../../redux/notification';
import {
	defaultSegments,
	sortSegments,
	canDeleteSegment,
	validateSegments,
	formatRange,
	toPayload,
	INVALID_TIME_MESSAGE
} from './segmentUtils';

import './SplitShift.scss';
import { loadUser, facilitiesEnabled } from '../../../redux/user';

// TODO: SPLIT_SHIFTS_MAX_SEGMENTS is server-configurable (default 4). Surface it from the
// backend (alongside allow_split_shifts) and thread it in as a prop instead of hardcoding here.
const MAX_SEGMENTS = 4;
const REMOVE_UNIT_OPTION = { label: '- Remove Unit -', value: null };

const resolveUnitLabel = (units, unitId) => {
	if (!unitId) {
		return '-';
	}
	const unit = units.find((item) => item.id === unitId);
	return unit ? unit.unit_name : '-';
};

const SplitShiftModal = ({ show, onHide, slot, units = [] }) => {
	const userFacilities = useSelector(facilitiesEnabled, shallowEqual);
	const shiftStart = useMemo(() => moment.tz(slot.shift_start, slot.facility_timezone), [
		slot.shift_start,
		slot.facility_timezone
	]);
	const shiftEnd = useMemo(() => moment.tz(slot.shift_end, slot.facility_timezone), [
		slot.shift_end,
		slot.facility_timezone
	]);
	const rowIdRef = useRef(0);

	const shouldShowUnits = useMemo(() => {
		const facility = userFacilities.find(
			(userFacility) => parseInt(userFacility.id, 10) === parseInt(slot.facility_id, 10)
		);
		 
		return !!facility?.portal_units_enabled;
	}, [userFacilities, slot.facility_id]);

	const buildSegments = useCallback(() => {
		rowIdRef.current = 0;
		return defaultSegments(shiftStart.clone(), shiftEnd.clone()).map((segment) => {
			rowIdRef.current += 1;
			return { ...segment, rowId: rowIdRef.current };
		});
	}, [shiftStart, shiftEnd]);

	const [segments, setSegments] = useState(buildSegments);
	const [errors, setErrors] = useState({});
	const [isConfirming, setIsConfirming] = useState(false);
	const [splitShift, { isLoading }] = useSplitShiftMutation();
	const dispatch = useDispatch();

	useEffect(() => {
		setSegments(buildSegments());
		setErrors({});
		setIsConfirming(false);
	}, [buildSegments]);

	const unitOptions = useMemo(
		() => [REMOVE_UNIT_OPTION, ...units.map((unit) => ({ label: unit.unit_name, value: unit.id }))],
		[units]
	);
	const sortedSegments = useMemo(() => sortSegments(segments), [segments]);

	const confirmationRows = useMemo(
		() =>
			sortedSegments.map((segment, index) => ({
				segmentNumber: index + 1,
				time: formatRange(segment),
				unit: resolveUnitLabel(units, segment.unitId)
			})),
		[sortedSegments, units]
	);

	const setSegmentTimeError = (index, field) => {
		setErrors((prev) => ({ ...prev, [`segments[${index}].${field}Time`]: INVALID_TIME_MESSAGE }));
	};

	const handleStartChange = (index, value) => {
		const previousSegment = segments[index - 1];
		const fillPreviousEnd = value && previousSegment && (!previousSegment.end || !previousSegment.end.isValid());
		setSegments(
			segments.map((segment, i) => {
				if (i === index) {
					return { ...segment, start: value };
				}
				if (i === index - 1 && fillPreviousEnd) {
					return { ...segment, end: value.clone() };
				}
				return segment;
			})
		);
		setErrors((prev) => {
			const next = { ...prev };
			delete next[`segments[${index}].startTime`];
			const previousEndKey = `segments[${index - 1}].endTime`;
			// The previous row's "Enter a valid time" flag refers to unparseable text still sitting in its
			// picker, which this edit didn't touch — keep it. The exception is the auto-fill above, which
			// replaces that picker's value (and its displayed text) with a valid time.
			if (fillPreviousEnd || next[previousEndKey] !== INVALID_TIME_MESSAGE) {
				delete next[previousEndKey];
			}
			return next;
		});
	};

	const handleEndChange = (index, value) => {
		const nextSegment = segments[index + 1];
		const fillNextStart = value && nextSegment && (!nextSegment.start || !nextSegment.start.isValid());
		setSegments(
			segments.map((segment, i) => {
				if (i === index) {
					return { ...segment, end: value };
				}
				if (i === index + 1 && fillNextStart) {
					return { ...segment, start: value.clone() };
				}
				return segment;
			})
		);
		setErrors((prev) => {
			const next = { ...prev };
			delete next[`segments[${index}].endTime`];
			const nextStartKey = `segments[${index + 1}].startTime`;
			// The next row's "Enter a valid time" flag refers to unparseable text still sitting in its
			// picker, which this edit didn't touch — keep it. The exception is the auto-fill above, which
			// replaces that picker's value (and its displayed text) with a valid time.
			if (fillNextStart || next[nextStartKey] !== INVALID_TIME_MESSAGE) {
				delete next[nextStartKey];
			}
			return next;
		});
	};
	const handleStartError = (index) => setSegmentTimeError(index, 'start');
	const handleEndError = (index) => setSegmentTimeError(index, 'end');

	const handleUnitChange = (index, { unitId }) => {
		const updated = segments.map((segment, i) => (i === index ? { ...segment, unitId: unitId || null } : segment));
		setSegments(updated);
	};

	const handleAddSegment = () => {
		rowIdRef.current += 1;
		// Insert the new segment just before the last one (first and last stay fixed).
		// Pre-fill its start with the preceding segment's end (the contiguous boundary)
		// and leave its end blank for the user to fill.
		const insertIndex = segments.length - 1;
		const precedingSegment = segments[insertIndex - 1];
		const newSegment = {
			start: precedingSegment && precedingSegment.end ? precedingSegment.end.clone() : null,
			end: null,
			unitId: null,
			rowId: rowIdRef.current
		};
		// The last segment's start was the old boundary; the new segment now sits before it,
		// so re-anchor the last segment's start to the new segment's end.
		const lastSegment = segments[insertIndex];
		const updatedLast = { ...lastSegment, start: newSegment.end ? newSegment.end.clone() : null };
		setSegments([...segments.slice(0, insertIndex), newSegment, updatedLast]);
	};

	const handleDeleteSegment = (index) => {
		const remaining = segments.filter((segment, i) => i !== index);
		// Error keys are index-based, so re-check the remaining pickers while re-indexing them:
		// carry each surviving row's invalid-time flag over to its new index and drop everything else.
		const nextErrors = {};
		// Re-anchor the new last segment's end to the shift end so the shift stays covered
		// even when the previously-last segment is the one removed.
		const reanchored = remaining.map((segment, newIndex) => {
			const previousIndex = newIndex >= index ? newIndex + 1 : newIndex;
			const isNewLast = newIndex === remaining.length - 1;
			if (errors[`segments[${previousIndex}].startTime`] === INVALID_TIME_MESSAGE) {
				nextErrors[`segments[${newIndex}].startTime`] = INVALID_TIME_MESSAGE;
			}
			// The new last segment's end is re-anchored to the shift end below (and its picker is
			// disabled), so any invalid-time flag it carried no longer applies.
			if (!isNewLast && errors[`segments[${previousIndex}].endTime`] === INVALID_TIME_MESSAGE) {
				nextErrors[`segments[${newIndex}].endTime`] = INVALID_TIME_MESSAGE;
			}
			return isNewLast ? { ...segment, end: shiftEnd.clone() } : segment;
		});
		setSegments(reanchored);
		setErrors(nextErrors);
	};
	const handleSplit = () => {
		const { isValid, errors: validationErrors, rangeError } = validateSegments(segments, shiftStart, shiftEnd);
		const invalidTimeErrors = {};
		Object.keys(errors).forEach((key) => {
			if (errors[key] === INVALID_TIME_MESSAGE) {
				invalidTimeErrors[key] = INVALID_TIME_MESSAGE;
			}
		});
		setErrors({ ...validationErrors, ...invalidTimeErrors });
		if (rangeError) {
			dispatch(showNotification('error', rangeError));
		}
		if (!isValid || Object.keys(invalidTimeErrors).length > 0) {
			return;
		}
		setIsConfirming(true);
	};

	const handleGoBack = () => setIsConfirming(false);

	const handleConfirm = async () => {
		try {
			await splitShift({ slot, segments: toPayload(sortedSegments) }).unwrap();
			dispatch(showNotification('success', 'Shift split successfully.'));
			onHide(true);
		} catch (error) {
			// Handled errors (that have the segment that failed on the field) carry field-level validation messages we surface back on the edit
			// step. Anything else is unexpected: close the modal and show an error toast.
			const apiErrors = error && error.data ? error.data.errors : null;
			const mapped = {};
			if (Array.isArray(apiErrors)) {
				apiErrors.forEach((item) => {
					if (item.field?.includes('segments')) {
						mapped[item.field] = item.message;
					}
				});
			}
			if (Object.keys(mapped).length > 0) {
				setErrors(mapped);
				setIsConfirming(false);
				return;
			}
			const [{ field, message }] = apiErrors;
			if (field === 'unit_id') {
				dispatch(loadUser());
			}
			dispatch(showNotification('error', message));
			onHide(true);
		}
	};

	const addDisabled = segments.length >= MAX_SEGMENTS;

	return (
		<Modal
			size="lg"
			centered
			show={show}
			onHide={onHide}
			className="split-shift-modal"
			backdrop="static"
			keyboard={false}
		>
			<Modal.Header closeButton>
				<Modal.Title>{isConfirming ? 'Confirm Split shift' : 'Split shift'}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<h2>Current</h2>
				<div className="split-shift-current">
					<div className="split-shift-current-col">
						<div>
							Shift type: <b>{slot.shift_type || '-'}</b>
						</div>
						<div>
							Date: <b>{slot.shift_date_label || '-'}</b>
						</div>
						<div>
							Shift time: <b>{slot.shift_time_label || '-'}</b>
						</div>
					</div>
					<div className="split-shift-current-col">
						<div>
							Professional: <b>{slot.professional_name || '-'}</b>
						</div>
						<div>
							Facility: <b>{slot.facility_name || '-'}</b>
						</div>
					</div>
				</div>
				{isConfirming ? (
					<>
						<h2>Updates</h2>
						<table className="split-shift-updates">
							<thead>
								<tr>
									<th>Segment</th>
									<th>Time</th>
									{shouldShowUnits && <th>Unit</th>}
								</tr>
							</thead>
							<tbody>
								{confirmationRows.map((row) => (
									<tr key={`segment-${row.segmentNumber}`}>
										<td>{row.segmentNumber}</td>
										<td>{row.time}</td>
										{shouldShowUnits && <td>{row.unit}</td>}
									</tr>
								))}
							</tbody>
						</table>
					</>
				) : (
					<>
						<h2>Segments</h2>
						<div className="split-shift-segments">
							{segments.map((segment, index) => (
								<SplitShiftSegmentRow
									key={segment.rowId}
									index={index}
									number={index + 1}
									segment={segment}
									shiftStart={shiftStart}
									shiftEnd={shiftEnd}
									isFirst={index === 0}
									isLast={index === segments.length - 1}
									canDelete={canDeleteSegment(index, segments.length)}
									unitOptions={unitOptions}
									startError={errors[`segments[${index}].startTime`]}
									endError={errors[`segments[${index}].endTime`]}
									onStartChange={handleStartChange}
									onEndChange={handleEndChange}
									onStartError={handleStartError}
									onEndError={handleEndError}
									onUnitChange={handleUnitChange}
									onDelete={handleDeleteSegment}
									shouldShowUnits={shouldShowUnits}
								/>
							))}
						</div>
						<button
							type="button"
							className="btn-light-purple split-shift-add"
							onClick={handleAddSegment}
							disabled={addDisabled}
						>
							{' '}
							<img src={addDisabled ? ArrowSplitIconWhite : ArrowSplitIconPurple} alt="Add Segment" />
							Add segment ({segments.length}/{MAX_SEGMENTS})
						</button>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				{isConfirming ? (
					<>
						<button type="button" className="btn-light" onClick={handleGoBack}>
							Go back
						</button>
						<button type="button" className="btn-purple" onClick={handleConfirm} disabled={isLoading}>
							Confirm Split
						</button>
					</>
				) : (
					<>
						<button type="button" className="btn-light" onClick={onHide}>
							Cancel
						</button>
						<button type="button" className="btn-purple" onClick={handleSplit}>
							Split shift
						</button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default memo(SplitShiftModal);
